import React, { useContext, useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes, faArrowUp, faArrowDown, faCode, faPaste } from '@fortawesome/free-solid-svg-icons';
import './styles/CustomizedVariable.css'; 
import axios from 'axios';
import config from '../configs/config';
import VariableContext from '../contexts/VariableContext';

const CustomizedVariable = ({ isOpen, onClose, setcurrentSelectedVariablesset, projectId }) => {
  const { dataGroup, setdataGroup,    selectedProject,
    selectedClient,powner
 } = useContext(VariableContext);
  const [rows, setRows] = useState([{ stub: '', logic: '' }]);
  const [variableName, setVariableName] = useState('');
  const [loopVarName, setLoopVarName] = useState('');
  const [availableVariables, setAvailableVariables] = useState([]);
  const [showVariableSelector, setShowVariableSelector] = useState(false);
  const [currentRowIndex, setCurrentRowIndex] = useState(0);
  const [operationType, setOperationType] = useState('OR');
  const [selectedVariables, setSelectedVariables] = useState([]);
  const [advancedMode, setAdvancedMode] = useState(false);
  const [loopVar, setLoopVar] = useState(false);
  const [showPasteModal, setShowPasteModal] = useState(false);
  const [pasteData, setPasteData] = useState('');
  const fileInputRef = useRef(null);
const [isSaving, setIsSaving] = useState(false);
const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    // Extract available variables from dataGroup when component mounts
    if (dataGroup) {
      setAvailableVariables(Object.keys(dataGroup).filter(v => !v.startsWith('_')));
    }
  }, [dataGroup]);

  if (!isOpen) return null;

  const handleAddRow = () => {
    setRows([...rows, { stub: '', logic: '' }]);
  };

  const handleRemoveRow = (index) => {
    if (rows.length > 1) {
      const newRows = [...rows];
      newRows.splice(index, 1);
      setRows(newRows);
    }
  };

  const handleMoveUp = (index) => {
    if (index > 0) {
      const newRows = [...rows];
      [newRows[index], newRows[index - 1]] = [newRows[index - 1], newRows[index]];
      setRows(newRows);
    }
  };

  const handleMoveDown = (index) => {
    if (index < rows.length - 1) {
      const newRows = [...rows];
      [newRows[index], newRows[index + 1]] = [newRows[index + 1], newRows[index]];
      setRows(newRows);
    }
  };

  const handleInputChange = (index, field, value) => {
    const newRows = [...rows];
    newRows[index][field] = value;
    setRows(newRows);
  };

  const openVariableSelector = (index) => {
    setCurrentRowIndex(index);
    setShowVariableSelector(true);
    setSelectedVariables([]);
  };

  const toggleVariableSelection = (variable) => {
    setSelectedVariables(prev => 
      prev.includes(variable) 
        ? prev.filter(v => v !== variable) 
        : [...prev, variable]
    );
  };

  const applyVariableOperation = () => {
    if (selectedVariables.length === 0) return;
    
    let logicExpression = '';
    if (advancedMode) {
      // In advanced mode, just use the current logic
      logicExpression = rows[currentRowIndex].logic;
    } else {
      // In simple mode, build the expression based on selected variables and operation
      const operator = operationType === 'OR' ? ' or ' : 
                      operationType === 'AND' ? ' and ' : 
                      ' and not ';
      
      logicExpression = selectedVariables
        .map((v, i) => {
          if (operationType === 'NOT' && i === selectedVariables.length - 1) {
            return `not ${v}`;
          }
          return v;
        })
        .join(operator);
    }

    const newRows = [...rows];
    newRows[currentRowIndex].logic = logicExpression;
    setRows(newRows);
    setShowVariableSelector(false);
  };

  // Parse Excel/TSV paste data
  const parsePastedData = (text) => {
    // Split by new lines
    const lines = text.trim().split(/\r?\n/);
    const parsedRows = [];
    
    for (const line of lines) {
      // Try to split by tab first (Excel default), then by comma
      let columns = line.split(/\t/);
      if (columns.length === 1) {
        columns = line.split(/,/);
      }
      
      // Clean up each column value
      const stub = columns[0] ? columns[0].trim() : '';
      const logic = columns[1] ? columns[1].trim() : '';
      
      // Only add row if at least one column has content
      if (stub || logic) {
        parsedRows.push({ stub, logic });
      }
    }
    
    return parsedRows;
  };

  const handlePasteFromExcel = () => {
    if (!pasteData.trim()) {
      alert('Please paste some data first');
      return;
    }
    
    const newRows = parsePastedData(pasteData);
    
    if (newRows.length === 0) {
      alert('No valid data found. Please ensure your data has at least one column.');
      return;
    }
    
    // Replace existing rows or append?
    const replaceExisting = window.confirm(
      `Found ${newRows.length} row(s) to paste.\n\nClick OK to REPLACE existing rows.\nClick Cancel to APPEND to existing rows.`
    );
    
    if (replaceExisting) {
      setRows(newRows);
    } else {
      setRows([...rows, ...newRows]);
    }
    
    setShowPasteModal(false);
    setPasteData('');
    alert(`Successfully added ${newRows.length} row(s)!`);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      setPasteData(content);
      // Auto-parse and preview
      const previewRows = parsePastedData(content);
      if (previewRows.length > 0) {
        alert(`Loaded ${previewRows.length} row(s) from file. Click "Apply Paste" to confirm.`);
      }
    };
    reader.readAsText(file);
    event.target.value = ''; // Reset file input
  };

const handleSave = async () => {
  // Reset error state
  setSaveError(null);
  
  // Validation checks before proceeding
  if (!variableName || variableName.trim() === '') {
    alert('Please enter a variable name');
    return;
  }
  
  // Check if variable name already exists
  if (dataGroup && dataGroup[variableName]) {
    alert(`Variable "${variableName}" already exists. Please use a different name.`);
    return;
  }
  
  // Check if any rows have empty stubs or logic
  const emptyRows = rows.filter(row => !row.stub.trim() || !row.logic.trim());
  if (emptyRows.length > 0) {
    const confirmEmpty = window.confirm(
      `${emptyRows.length} row(s) have empty Group Label or Logic Definition.\n\nDo you want to continue anyway?`
    );
    if (!confirmEmpty) return;
  }
  
  setIsSaving(true);
  
  try {
    // Calculate max location
    let maxLoc = 0;
    if (dataGroup) {
      Object.values(dataGroup).forEach(q => {
        (q.varName || []).forEach(v => {
          if (v.Location && v.Location.includes(":")) {
            const [, end] = v.Location.split(":").map(Number);
            if (end > maxLoc) maxLoc = end;
          }
        });
      });
    }
    
    // Prepare custom variable object
    const customVariable = {
      name: variableName.trim(),
      type: 'SingleResponse',
      logic: rows.map(row => ({
        stub: row.stub.trim(),
        condition: row.logic.trim()
      }))
    };
    
    // Update local state first (optimistic update)
    setcurrentSelectedVariablesset(prev => [...prev, customVariable.name]);
    
    // Prepare value labels and values for local state update
    const valueLabels = {};
    const values = rows.map((row, index) => {
      const value = (index + 1).toString() + ".0";
      valueLabels[value] = row.stub;
      return {
        index: index + 1,
        title: row.stub
      };
    });
    
    // Make API call to backend
    let response = null;
    let apiError = null;
    
    try {
      response = await axios.post(`${config.API_BASE_URL}/compute-newvar`, {
        projectId: projectId,
        customVariable: customVariable,
        dataGroup: dataGroup,
        project: JSON.stringify(selectedProject),
        client: JSON.stringify(selectedClient),
      });
      
      if (!response.data || !response.data.success) {
        throw new Error(response.data?.message || 'Failed to create custom variable on server');
      }
      
    } catch (err) {
      apiError = err;
      console.error('API Error:', err);
      
      // Provide specific error messages based on error type
      if (err.code === 'ECONNABORTED') {
        throw new Error('Request timed out. Please check your connection and try again.');
      } else if (err.response) {
        // Server responded with error status
        const status = err.response.status;
        if (status === 400) {
          throw new Error('Invalid data format. Please check your variable logic.');
        } else if (status === 401 || status === 403) {
          throw new Error('You don\'t have permission to create custom variables.');
        } else if (status === 404) {
          throw new Error('Server endpoint not found. Please contact support.');
        } else if (status >= 500) {
          throw new Error('Server error. Please try again later.');
        } else {
          throw new Error(err.response.data?.message || `Server error (${status})`);
        }
      } else if (err.request) {
        // Request made but no response
        throw new Error('No response from server. Please check your network connection.');
      } else {
        throw new Error(err.message || 'An unexpected error occurred');
      }
    }
    
    // Only update dataGroup if API call was successful
    if (response && response.data && response.data.success) {
      // Update dataGroup with new variable
      setdataGroup({
        ...dataGroup,
        [variableName]: {
          "Question number": variableName,
          Qtitle: variableName,
          instructions: "",
          type: "SingleResponse",
          values: values,
          valueLabels: valueLabels,
          varName: [{ Name: variableName, Location: `${maxLoc + 1}:${maxLoc + 2}` }],
          "order": Object.keys(dataGroup).length + 1,
          "_order": Object.keys(dataGroup).length + 1
        }
      });
      
      // Show success message
      alert(`Custom variable "${variableName}" created successfully!`);
      
      // Close the modal
      onClose();
    } else {
      throw new Error(response?.data?.message || 'Failed to create custom variable');
    }
    
  } catch (error) {
    console.error('Error creating custom variable:', error);
    setSaveError(error.message);
    
    // Show user-friendly error message
    let errorMessage = error.message || 'Error creating custom variable';
    
    // Add helpful suggestions based on error type
    if (errorMessage.includes('variable name')) {
      errorMessage += '\n\nPlease check that the variable name is valid and not already in use.';
    } else if (errorMessage.includes('logic')) {
      errorMessage += '\n\nPlease check your logic expressions for syntax errors.';
    } else if (errorMessage.includes('network') || errorMessage.includes('connection')) {
      errorMessage += '\n\nPlease check your internet connection and try again.';
    }
    
    alert(errorMessage);
    
    // Rollback optimistic update if needed
    setcurrentSelectedVariablesset(prev => prev.filter(v => v !== variableName));
    
  } finally {
    setIsSaving(false);
  }
};

// Also add a helper function to test the backend connection
const testBackendConnection = async () => {
  try {
    const response = await axios.get(`${config.API_BASE_URL}/health`, { timeout: 5000 });
    return response.status === 200;
  } catch (error) {
    console.error('Backend connection test failed:', error);
    return false;
  }
};

// Optional: Add a retry function for failed saves
const retrySave = async () => {
  if (saveError) {
    setSaveError(null);
    await handleSave();
  }
};



  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Create Custom Variable</h3>
          <button onClick={onClose} className="modal-close-button">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="variable-name-input">
          <label>Variable Name:</label>
          <input
            type="text"
            value={variableName}
            onChange={(e) => setVariableName(e.target.value)}
            placeholder="Enter variable name"
          />
        </div>
<label className="toggle-item">
                    <input
                      type="checkbox"
                      checked={loopVar}
                      onChange={(e) => setLoopVar(e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                    <span className="toggle-text">Bilendi Setup</span>
                  </label>
                          {loopVar && <div className="variable-name-input">
          <label>Loop by Name:</label>
           <input
            type="text"
            value={loopVarName}
            onChange={(e) => setLoopVarName(e.target.value)}
            placeholder="Enter variable name"
          />
        </div>}
        <div className="table-container">
          <div className="table-toolbar">
            <button 
              onClick={() => setShowPasteModal(true)} 
              className="paste-excel-btn"
              title="Paste from Excel"
            >
              <FontAwesomeIcon icon={faPaste} /> Paste from Excel
            </button>
          </div>
          <table className="custom-var-table">
            <thead>
              <tr>
                <th>Group Label</th>
                <th>Logic Definition</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={index}>
                  <td>
                    <input
                      type="text"
                      value={row.stub}
                      onChange={(e) => handleInputChange(index, 'stub', e.target.value)}
                      placeholder="Group label"
                    />
                  </td>
                  <td>
                    <div className="logic-input-container">
                      <input
                        type="text"
                        value={row.logic}
                        onChange={(e) => handleInputChange(index, 'logic', e.target.value)}
                        placeholder="e.g., Q1 or Q2"
                      />
                      <button 
                        className="variable-selector-btn"
                        onClick={() => openVariableSelector(index)}
                        title="Select variables"
                      >
                        <FontAwesomeIcon icon={faCode} />
                      </button>
                    </div>
                  </td>
                  <td className="actions-cell">
                    <button 
                      onClick={() => handleMoveUp(index)} 
                      disabled={index === 0}
                      title="Move up"
                    >
                      <FontAwesomeIcon icon={faArrowUp} />
                    </button>
                    <button 
                      onClick={() => handleMoveDown(index)} 
                      disabled={index === rows.length - 1}
                      title="Move down"
                    >
                      <FontAwesomeIcon icon={faArrowDown} />
                    </button>
                    <button 
                      onClick={() => handleRemoveRow(index)} 
                      disabled={rows.length <= 1}
                      title="Remove row"
                      className="remove-btn"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </td>
                 </tr>
              ))}
            </tbody>
           </table>
        </div>

<div className="modal-footer">
  <div className="footer-buttons">
    <button onClick={handleAddRow} className="add-row-btn" disabled={isSaving}>
      <FontAwesomeIcon icon={faPlus} /> Add Row
    </button>
    <button 
      onClick={handleSave} 
      className={`save-btn ${isSaving ? 'saving' : ''}`}
      disabled={isSaving}
    >
      {isSaving ? (
        <>
          <span className="spinner"></span> Saving...
        </>
      ) : (
        'Save Custom Variable'
      )}
    </button>
  </div>
  {saveError && (
    <div className="error-message">
      <span className="error-text">{saveError}</span>
      <button onClick={retrySave} className="retry-btn">
        Retry
      </button>
    </div>
  )}
</div>

        {/* Paste from Excel Modal */}
        {showPasteModal && (
          <div className="variable-selector-modal">
            <div className="variable-selector-content paste-modal">
              <h3>Paste from Excel</h3>
              
              <div className="paste-instructions">
                <p><strong>Instructions:</strong></p>
                <ul>
                  <li>Copy cells from Excel (2 columns: Group Label and Logic Definition)</li>
                  <li>Paste the data in the text area below</li>
                  <li>Click "Apply Paste" to add the rows</li>
                  <li>You can also upload a CSV or TSV file</li>
                </ul>
                <p><small>Tip: Excel data is copied as tab-separated values</small></p>
              </div>

              <div className="paste-textarea-container">
                <textarea
                  value={pasteData}
                  onChange={(e) => setPasteData(e.target.value)}
                  placeholder="Paste your Excel data here...&#10;&#10;Example:&#10;Group 1&#9;Q1 or Q2&#10;Group 2&#9;Q3 and Q4&#10;Group 3&#9;not Q5"
                  rows={8}
                />
              </div>

              <div className="file-upload-section">
                <label className="file-upload-label">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept=".csv,.tsv,.txt,.xlsx"
                    style={{ display: 'none' }}
                  />
                  <button 
                    type="button"
                    onClick={() => fileInputRef.current.click()}
                    className="upload-file-btn"
                  >
                    Or Upload File
                  </button>
                </label>
              </div>

              {pasteData && (
                <div className="paste-preview">
                  <h4>Preview (first 5 rows):</h4>
                  <div className="preview-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Group Label</th>
                          <th>Logic Definition</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsePastedData(pasteData).slice(0, 5).map((row, idx) => (
                          <tr key={idx}>
                            <td>{row.stub || '(empty)'}</td>
                            <td>{row.logic || '(empty)'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {parsePastedData(pasteData).length > 5 && (
                      <p><small>... and {parsePastedData(pasteData).length - 5} more row(s)</small></p>
                    )}
                  </div>
                </div>
              )}

              <div className="variable-selector-buttons">
                <button onClick={() => {
                  setShowPasteModal(false);
                  setPasteData('');
                }} className="cancel-btn">
                  Cancel
                </button>
                <button onClick={handlePasteFromExcel} className="apply-btn">
                  Apply Paste
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Variable Selector Modal */}
        {showVariableSelector && (
          <div className="variable-selector-modal">
            <div className="variable-selector-content">
              <h3>Combine Variables</h3>
              
              <div className="operation-selector">
                <label>
                  <input
                    type="radio"
                    checked={!advancedMode}
                    onChange={() => setAdvancedMode(false)}
                  />
                  Simple Mode
                </label>
                <label>
                  <input
                    type="radio"
                    checked={advancedMode}
                    onChange={() => setAdvancedMode(true)}
                  />
                  Advanced Mode
                </label>
              </div>

              {!advancedMode ? (
                <>
                  <div className="operation-type">
                    <select 
                      value={operationType} 
                      onChange={(e) => setOperationType(e.target.value)}
                    >
                      <option value="OR">OR (Any of the selected)</option>
                      <option value="AND">AND (All of the selected)</option>
                      <option value="NOT">AND NOT (All except last)</option>
                    </select>
                  </div>

                  <div className="variable-list">
                    {availableVariables.map(variable => (
                      <div 
                        key={variable} 
                        className={`variable-item ${selectedVariables.includes(variable) ? 'selected' : ''}`}
                        onClick={() => toggleVariableSelection(variable)}
                      >
                        {variable}
                      </div>
                    ))}
                  </div>

                  <div className="preview-section">
                    <h4>Preview:</h4>
                    <div className="preview-logic">
                      {selectedVariables.length > 0 ? (
                        selectedVariables.map((v, i) => (
                          <React.Fragment key={v}>
                            {i > 0 && (
                              <span className="operator">
                                {operationType === 'OR' ? 'OR' : 
                                 operationType === 'AND' ? 'AND' : 
                                 i === selectedVariables.length - 1 ? 'AND NOT' : 'AND'}
                              </span>
                            )}
                            <span className="variable">{v}</span>
                          </React.Fragment>
                        ))
                      ) : (
                        <span className="no-variables">No variables selected</span>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="advanced-mode">
                  <h4>Advanced Logic Builder</h4>
                  <textarea
                    value={rows[currentRowIndex].logic}
                    onChange={(e) => handleInputChange(currentRowIndex, 'logic', e.target.value)}
                    placeholder="Enter custom logic (e.g., (Q1 or Q2) and not Q3)"
                    rows={5}
                  />
                  <div className="available-vars-hint">
                    <p>Available variables: {availableVariables.join(', ')}</p>
                  </div>
                </div>
              )}

              <div className="variable-selector-buttons">
                <button onClick={() => setShowVariableSelector(false)} className="cancel-btn">
                  Cancel
                </button>
                <button onClick={applyVariableOperation} className="apply-btn">
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomizedVariable;